import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, CalendarClock } from 'lucide-react';

const toLocalInputValue = (offsetMinutes) => {
  // Build a local-time string in the format <input type="datetime-local"> expects.
  const d = new Date(Date.now() + offsetMinutes * 60_000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * NextTouchModal — schedules when the team should come back to a task.
 *
 * Used in Daily Run / Today's "Set next touch" affordance. The task
 * surfaces in the Today dashboard's Follow-ups count + section once
 * next_touch_at falls on today.
 */
export const NextTouchModal = ({ open, onOpenChange, task, onConfirm }) => {
  const [nextTouchAt, setNextTouchAt] = useState(() => toLocalInputValue(60));
  const [reason, setReason] = useState('');
  const [waitingOn, setWaitingOn] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      // Default to 1h from now (or to the task's existing next_touch_at if set)
      if (task?.next_touch_at) {
        const d = new Date(task.next_touch_at);
        const pad = (n) => String(n).padStart(2, '0');
        setNextTouchAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
      } else {
        setNextTouchAt(toLocalInputValue(60));
      }
      setReason(task?.next_touch_reason || '');
      setWaitingOn(task?.waiting_on || '');
    }
  }, [open, task?.id, task?.next_touch_at, task?.next_touch_reason, task?.waiting_on]);

  const quickShift = (minutes) => setNextTouchAt(toLocalInputValue(minutes));

  const handleConfirm = async () => {
    if (!nextTouchAt) return;
    setSubmitting(true);
    try {
      const iso = new Date(nextTouchAt).toISOString();
      await onConfirm({
        next_touch_at: iso,
        next_touch_reason: reason.trim() || null,
        waiting_on: waitingOn.trim() || null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const taskTitle = task?.title || task?.summary || 'this task';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="next-touch-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-[#3F617E]" />
            Set next touch
          </DialogTitle>
          <DialogDescription>
            When should the team come back to <span className="font-medium text-stone-700">{taskTitle}</span>?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs">Next touch</Label>
            <Input
              type="datetime-local"
              value={nextTouchAt}
              onChange={(e) => setNextTouchAt(e.target.value)}
              className="h-9 text-sm"
              data-testid="next-touch-at-input"
            />
            <div className="flex flex-wrap gap-1.5 pt-1">
              {[
                ['+30m', 30], ['+1h', 60], ['+2h', 120], ['+4h', 240],
                ['Tomorrow', 24 * 60],
              ].map(([label, mins]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => quickShift(mins)}
                  className="text-[10px] px-2 py-1 rounded-full border border-stone-200 hover:bg-stone-100 text-stone-600 transition"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Confirm tour cost with vendor"
              className="min-h-[60px] text-sm"
              data-testid="next-touch-reason-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Waiting on (optional)</Label>
            <Input
              value={waitingOn}
              onChange={(e) => setWaitingOn(e.target.value)}
              placeholder="Guest / Vendor / Team — who's holding this up?"
              className="h-9 text-sm"
              data-testid="next-touch-waiting-on-input"
            />
            <p className="text-[10px] text-stone-400 leading-snug">
              Filling this moves the task into Waiting On.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="rounded-full"
            data-testid="next-touch-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || !nextTouchAt}
            className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
            data-testid="next-touch-confirm"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CalendarClock className="h-4 w-4 mr-1" />}
            Set next touch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NextTouchModal;
